/**
 * Shared site navigation data — used by SiteHeader (mega menu) and SiteFooter
 * (multi-column). Curated labels are clearer than page eyebrows.
 *
 * Completeness guarantee: getUncategorizedLandingLinks() finds any landingPages
 * entry NOT referenced below and returns it, so new pages are never lost from
 * the footer even if someone forgets to add them here.
 */
import { landingPages } from "./landingPages";

export type NavLink = { label: string; to: string; title?: string; hindi?: boolean };
export type NavGroup = { heading: string; links: NavLink[] };
export type MegaMenu = { label: string; columns: NavGroup[]; featured?: NavLink };

export const BUILDER_LINKS: NavLink[] = [
  { label: "Free Resume Builder", to: "/resume-builder" },
  { label: "ATS Resume Analyzer", to: "/resume-analyzer", title: "Free ATS resume checker and analyzer" },
  { label: "Resume Proofreader", to: "/resume-proofreader", title: "Free resume proofreader — grammar, impact and technical checks" },
  { label: "Interview Preparation", to: "/interview-preparation", title: "Interview preparation built around your role and experience" },
  { label: "Salary Negotiation", to: "/salary-negotiation", title: "CTC breakdown, negotiating position and scripts" },
  // Single cover-letter entry point in the nav. /cover-letter-builder is an SEO
  // landing page for the same job — it keeps a footer link via
  // getUncategorizedLandingLinks() rather than sitting next to a near-identical
  // label here, and its CTA points at this tool.
  { label: "Create a Cover Letter", to: "/cover-letter-generator", title: "Free AI cover letter generator" },
  { label: "ATS Resume Builder", to: "/ats-resume-builder" },
  { label: "CV Builder", to: "/cv-builder" },
  { label: "Free Resume Maker", to: "/free-resume-maker" },
  { label: "Professional Resume Builder", to: "/professional-resume-builder" },
];

export const TEMPLATE_LINKS: NavLink[] = [
  { label: "Resume Templates", to: "/templates" },
  { label: "Free Word Templates", to: "/resume-templates" },
  { label: "Resume Examples", to: "/resume-examples" },
  { label: "Download Word (.docx) — free", to: "/templates" },
];

export const FRESHER_LINKS: NavLink[] = [
  { label: "Resume Builder for Freshers", to: "/resume-builder-for-freshers" },
  { label: "Resume Builder for Students", to: "/resume-builder-for-students" },
  { label: "Resume Format for Freshers", to: "/resume-format-for-freshers" },
  { label: "Career Objective for Freshers", to: "/career-objective-for-freshers" },
  { label: "Resume Headline for Freshers", to: "/resume-headline-for-freshers" },
  { label: "Resume Summary for Freshers", to: "/resume-summary-for-freshers" },
];

export const ROLE_LINKS: NavLink[] = [
  { label: "Teacher Resume", to: "/teacher-resume-for-freshers" },
  { label: "Data Analyst Resume", to: "/data-analyst-resume-for-freshers" },
  { label: "Engineering Resume", to: "/engineering-resume-for-freshers" },
  { label: "Computer Science Resume", to: "/computer-science-resume-for-freshers" },
  { label: "MBA Resume", to: "/mba-resume-for-freshers" },
  { label: "Accountant Resume", to: "/accountant-resume-for-freshers" },
];

export const HINDI_LINK: NavLink = {
  label: "Resume Kaise Banaye (हिंदी)",
  to: "/resume-kaise-banaye",
  title: "Resume kaise banaye — step-by-step guide in Hindi",
  hindi: true,
};

export const JOBSEARCH_LINKS: NavLink[] = [
  { label: "Why Should We Hire You — Fresher", to: "/why-should-we-hire-you-fresher" },
  { label: "Email When Sending a Resume", to: "/what-to-write-in-email-when-sending-resume" },
  { label: "Upload Resume to LinkedIn", to: "/how-to-upload-resume-in-linkedin" },
  { label: "CV vs Resume", to: "/difference-between-cv-and-resume" },
  { label: "CV Full Form (Hindi)", to: "/cv-full-form" },
  HINDI_LINK,
];

export const GUIDE_LINKS: NavLink[] = [
  { label: "How to Write a Resume", to: "/guides/how-to-write-a-resume" },
  { label: "ATS Resume Tips", to: "/guides/ats-resume-tips" },
  { label: "Resume Format Guide", to: "/guides/resume-format-guide" },
  { label: "Resume Summary Examples", to: "/guides/resume-summary-examples" },
  { label: "All Guides", to: "/guides" },
  { label: "Career Resources", to: "/resources" },
];

export const COMPANY_LINKS: NavLink[] = [
  { label: "Features & Free Pricing", to: "/features" },
  { label: "About AI Resume Studio", to: "/about" },
  { label: "Contact Us", to: "/contact" },
  { label: "Press & Media", to: "/press" },
  { label: "HTML Sitemap", to: "/sitemap-html" },
  { label: "Create a Free Account", to: "/signup" },
];

/** Header mega menu — grouped dropdowns. */
export const MEGA_MENU: MegaMenu[] = [
  {
    label: "Resume Builder",
    columns: [
      { heading: "Build", links: BUILDER_LINKS },
      { heading: "Templates & Examples", links: TEMPLATE_LINKS },
    ],
    featured: { label: "Browse all resume templates", to: "/templates" },
  },
  {
    label: "For Freshers",
    columns: [
      { heading: "Fresher essentials", links: FRESHER_LINKS },
      { heading: "By role", links: ROLE_LINKS },
    ],
    featured: { label: "Start a fresher resume free", to: "/resume-builder-for-freshers" },
  },
  {
    label: "Guides & Job Search",
    columns: [
      { heading: "Guides", links: GUIDE_LINKS },
      { heading: "Job search help", links: JOBSEARCH_LINKS },
    ],
    featured: { label: "Read the complete resume guide", to: "/guides/how-to-write-a-resume" },
  },
];

/** Any landingPages entry not referenced above (keeps footer complete). */
export function getUncategorizedLandingLinks(): NavLink[] {
  const referenced = new Set<string>([
    ...BUILDER_LINKS, ...TEMPLATE_LINKS, ...FRESHER_LINKS, ...ROLE_LINKS, ...JOBSEARCH_LINKS,
  ].map((l) => l.to));
  return landingPages
    .filter((p) => !referenced.has(p.slug))
    .map((p) => ({ label: p.eyebrow, to: p.slug, title: p.title }));
}
